import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { indexedDBAdapter } from '@/infrastructure/storage/IndexedDBAdapter';
import { searchVoices } from '@/infrastructure/elevenlabs/ElevenLabsClient';
import type { Scene, AppSettings, VideoSettings, GenerationStatus, CompletionDialogState, ErrorConfirmDialogState, SubtitleSettings, SubtitleStyle, VoiceInfo, VoiceFilter } from '@/types';
import { logger } from '@/lib/logger';
import { DEFAULT_SUBTITLE_SETTINGS } from '@/lib/constants';

interface AppStore {
  // Scenes
  scenes: Scene[];

  // Settings
  settings: AppSettings;
  videoSettings: VideoSettings;
  subtitleSettings: SubtitleSettings;
  globalSubtitleEnabled: boolean;

  // Generation
  generationStatus: GenerationStatus;

  // Voices
  voices: VoiceInfo[];
  voicesLoading: boolean;
  voicesError: string | null;
  voicesHasMore: boolean;
  voicesCurrentPage: number;
  voicesPageTokens: string[];
  voicesFetched: boolean;
  voicesFilter: VoiceFilter;

  // UI
  isSubtitleSettingsOpen: boolean;
  completionDialog: CompletionDialogState;
  errorConfirmDialog: ErrorConfirmDialogState;

  // Actions - Scenes
  addScenes: (images: { id: string; imageUrl: string }[]) => void;
  updateScene: (id: string, updates: Partial<Scene>) => void;
  deleteScene: (id: string) => void;
  reorderScenes: (sceneIds: string[]) => void;
  applyTexts: (texts: string[]) => void;
  deleteSceneAudio: (sceneId: string) => void;
  resetAll: () => Promise<void>;

  // Actions - Settings
  updateSettings: (updates: Partial<AppSettings>) => void;
  updateVideoSettings: (updates: Partial<VideoSettings>) => void;

  // Actions - Voices
  fetchVoices: (filter?: VoiceFilter) => Promise<void>;
  fetchVoicesPage: (direction: 'next' | 'prev') => Promise<void>;
  clearVoicesCache: () => void;

  // Actions - Subtitle Settings
  updateSubtitleSettings: (updates: Partial<Omit<SubtitleSettings, 'defaultStyle'>>) => void;
  updateSubtitleDefaultStyle: (updates: Partial<SubtitleStyle>) => void;
  setGlobalSubtitleEnabled: (enabled: boolean) => void;
  setSubtitleSettingsOpen: (open: boolean) => void;

  // Actions - Scene Subtitle
  updateSceneSubtitleText: (sceneId: string, text: string) => void;
  toggleSceneSubtitle: (sceneId: string, enabled: boolean) => void;
  updateSceneSubtitleStyle: (sceneId: string, style: Partial<SubtitleStyle>) => void;
  resetSceneSubtitleStyle: (sceneId: string) => void;
  resetAllSceneSubtitleStyles: () => void;
  copyTextToSubtitle: (sceneId: string) => void;
  copyAllTextsToSubtitles: () => void;

  // Actions - Generation
  setGenerationStatus: (status: Partial<GenerationStatus>) => void;
  resetGeneration: () => void;

  // Actions - Cancel
  cancelCallback: (() => void) | null;
  registerCancelCallback: (callback: (() => void) | null) => void;
  cancelGeneration: () => void;

  // Actions - Dialogs
  showCompletionDialog: (title: string, description: string) => void;
  hideCompletionDialog: () => void;
  showErrorConfirmDialog: (title: string, description: string) => Promise<boolean>;
  resolveErrorConfirmDialog: (shouldContinue: boolean) => void;
  hideErrorConfirmDialog: () => void;
}

const initialSettings: AppSettings = {
  elevenLabsApiKey: '',
  voiceId: '',
  voiceStability: 0.5,
  voiceSimilarityBoost: 0.75,
};

const initialVideoSettings: VideoSettings = {
  fps: 30,
  enableTransition: false,
  transitionSound: 'none',
  transitionColor: '#000000',
  defaultAdditionalTime: 0.5,
  enableAudioNormalization: true,
  targetLoudness: -16,
};

const initialGenerationStatus: GenerationStatus = {
  isGenerating: false,
  currentStep: 'idle',
  progress: 0,
  currentSceneIndex: 0,
  totalScenes: 0,
  message: '',
};

const initialCompletionDialog: CompletionDialogState = {
  open: false,
  title: '',
  description: '',
};

const initialErrorConfirmDialog: ErrorConfirmDialogState = {
  open: false,
  title: '',
  description: '',
  resolveCallback: undefined,
};

export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      immer((set, get) => {
        // Voice一覧を1ページ分取得して状態に反映する共通ローダー。
        // 検索・カテゴリはAPIパラメータとして渡し、ページネーションは絞り込み後の
        // 結果に対して行われる。取得中に別リクエストが走らないよう呼び出し側で
        // voicesLoadingをガードし、取得完了時にAPI Keyが変わっていた場合は破棄する。
        const loadVoicesPage = async (targetPage: number, pageToken?: string) => {
          const apiKey = get().settings.elevenLabsApiKey;
          const filter = get().voicesFilter;
          set({ voicesLoading: true, voicesError: null });

          try {
            const result = await searchVoices({
              apiKey,
              pageToken,
              search: filter.search,
              category: filter.category ?? undefined,
            });
            if (get().settings.elevenLabsApiKey !== apiKey) return;
            set((state) => {
              state.voices = result.voices;
              state.voicesHasMore = result.hasMore;
              state.voicesCurrentPage = targetPage;
              if (result.nextPageToken) {
                state.voicesPageTokens[targetPage] = result.nextPageToken;
              }
              state.voicesFetched = true;
              state.voicesLoading = false;

              // 選択中Voiceがこのページに含まれる場合、表示用の名前情報を最新化する
              //（過去バージョンで名前未保存のままの設定を自己修復する）
              const selected = result.voices.find((v) => v.voiceId === state.settings.voiceId);
              if (selected) {
                state.settings.voiceName = selected.name;
                state.settings.voiceCategory = selected.category;
              }
            });
          } catch (error) {
            logger.error('Voice一覧の取得に失敗しました', error as Error);
            if (get().settings.elevenLabsApiKey !== apiKey) return;
            set((state) => {
              state.voicesLoading = false;
              state.voicesError = 'Voice一覧の取得に失敗しました';
            });
          }
        };

        return {
        scenes: [],
        settings: initialSettings,
        videoSettings: initialVideoSettings,
        subtitleSettings: DEFAULT_SUBTITLE_SETTINGS,
        globalSubtitleEnabled: false,
        generationStatus: initialGenerationStatus,
        voices: [],
        voicesLoading: false,
        voicesError: null,
        voicesHasMore: false,
        voicesCurrentPage: 0,
        voicesPageTokens: [],
        voicesFetched: false,
        voicesFilter: { search: '', category: null },
        isSubtitleSettingsOpen: false,
        completionDialog: initialCompletionDialog,
        errorConfirmDialog: initialErrorConfirmDialog,
        cancelCallback: null,

        addScenes: (images) => {
          set((state) => {
            const startOrder = state.scenes.length;
            const newScenes: Scene[] = images.map((img, index) => ({
              id: img.id,
              order: startOrder + index,
              imageUrl: img.imageUrl,
              text: '',
              audioStatus: 'none',
              additionalDisplayTimeBefore: state.videoSettings.defaultAdditionalTime ?? 0.5,
              additionalDisplayTimeAfter: state.videoSettings.defaultAdditionalTime ?? 0.5,
              subtitleText: '',
              subtitleEnabled: true, // デフォルトでオン
            }));
            state.scenes.push(...newScenes);
          });
        },

        updateScene: (id, updates) => {
          set((state) => {
            const scene = state.scenes.find((s) => s.id === id);
            if (scene) {
              // NFD(分解形)の濁点・半濁点をNFC(合成形)に正規化
              const normalizedUpdates = updates.text !== undefined
                ? { ...updates, text: updates.text.normalize('NFC') }
                : updates;
              Object.assign(scene, normalizedUpdates);
            }
          });
        },

        deleteScene: (id) => {
          set((state) => {
            state.scenes = state.scenes.filter((s) => s.id !== id);
            state.scenes.forEach((scene, index) => {
              scene.order = index;
            });
          });
        },

        reorderScenes: (sceneIds) => {
          set((state) => {
            const sceneMap = new Map(state.scenes.map((s) => [s.id, s]));
            state.scenes = sceneIds
              .map((id) => sceneMap.get(id))
              .filter((s): s is Scene => s !== undefined);
            state.scenes.forEach((scene, index) => {
              scene.order = index;
            });
          });
        },

        applyTexts: (texts) => {
          const staleAudioSceneIds: string[] = [];
          set((state) => {
            const sortedScenes = [...state.scenes].sort((a, b) => a.order - b.order);
            sortedScenes.forEach((scene, index) => {
              const sceneInState = state.scenes.find((s) => s.id === scene.id);
              if (sceneInState) {
                const previousText = sceneInState.text;
                const newText = (texts[index] ?? '').normalize('NFC');
                sceneInState.text = newText;
                // テキスト変更時は音声が古くなるため再生成が必要
                if (sceneInState.audioUrl && newText !== previousText) {
                  if (sceneInState.audioUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(sceneInState.audioUrl);
                  }
                  sceneInState.audioStatus = 'none';
                  sceneInState.audioUrl = undefined;
                  sceneInState.audioDuration = undefined;
                  staleAudioSceneIds.push(sceneInState.id);
                }
              }
            });
          });
          staleAudioSceneIds.forEach((sceneId) => {
            indexedDBAdapter.deleteAudio(sceneId).catch((error) => {
              logger.error('IndexedDBからの音声削除に失敗しました', error as Error, { sceneId });
            });
          });
        },

        deleteSceneAudio: (sceneId) => {
          set((state) => {
            const scene = state.scenes.find((s) => s.id === sceneId);
            if (!scene) return;
            if (scene.audioUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(scene.audioUrl);
            }
            scene.audioStatus = 'none';
            scene.audioUrl = undefined;
            scene.audioDuration = undefined;
          });
          indexedDBAdapter.deleteAudio(sceneId).catch((error) => {
            logger.error('IndexedDBからの音声削除に失敗しました', error as Error, { sceneId });
          });
        },

        resetAll: async () => {
          const currentScenes: Scene[] = get().scenes;

          try {
            await indexedDBAdapter.clear();
            logger.info('リセット完了：IndexedDBをクリアしました');
          } catch (error) {
            logger.error('IndexedDBのクリアに失敗しました', error as Error);
          }

          set((state) => {
            currentScenes.forEach((scene: Scene) => {
              if (scene.imageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(scene.imageUrl);
              }
              if (scene.audioUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(scene.audioUrl);
              }
            });
            state.scenes = [];
            state.generationStatus = initialGenerationStatus;
          });
        },

        updateSettings: (updates) => {
          const previousApiKey = get().settings.elevenLabsApiKey;
          set((state) => {
            Object.assign(state.settings, updates);
          });
          // API Key変更時はVoiceキャッシュをリセット（選択済みvoiceIdは保持し、誤操作でも設定を失わないようにする）
          if (updates.elevenLabsApiKey !== undefined && updates.elevenLabsApiKey !== previousApiKey) {
            get().clearVoicesCache();
          }
        },

        updateVideoSettings: (updates) => {
          set((state) => {
            Object.assign(state.videoSettings, updates);
          });
        },

        // Voice Actions
        // filter指定あり: 条件が変わったときだけ先頭ページを取り直す（同条件の再呼び出しは無視）
        // filter指定なし: 現在の条件のまま先頭ページを強制再取得（エラー後の再試行用）
        fetchVoices: async (filter) => {
          const { settings, voicesLoading, voicesFilter, voicesFetched } = get();
          if (!settings.elevenLabsApiKey || voicesLoading) return;

          if (filter) {
            const isSameFilter =
              filter.search === voicesFilter.search && filter.category === voicesFilter.category;
            if (isSameFilter && voicesFetched) return;
            set({ voicesFilter: filter });
          }

          // 先頭ページの取得時は過去のページトークンを破棄する
          set({ voicesPageTokens: [] });
          await loadVoicesPage(0);
        },

        fetchVoicesPage: async (direction: 'next' | 'prev') => {
          const { settings, voicesCurrentPage, voicesPageTokens, voicesLoading } = get();
          if (!settings.elevenLabsApiKey || voicesLoading) return;

          if (direction === 'prev' && voicesCurrentPage <= 0) return;
          if (direction === 'next' && !voicesPageTokens[voicesCurrentPage]) return;

          const targetPage = direction === 'next' ? voicesCurrentPage + 1 : voicesCurrentPage - 1;
          // pageTokens[i] = page iから次ページへ進むためのトークン
          // page N にアクセスするには pageTokens[N-1] を使う（N>0の場合）
          const pageToken = targetPage > 0 ? voicesPageTokens[targetPage - 1] : undefined;
          await loadVoicesPage(targetPage, pageToken);
        },

        clearVoicesCache: () => {
          set((state) => {
            state.voices = [];
            state.voicesHasMore = false;
            state.voicesCurrentPage = 0;
            state.voicesPageTokens = [];
            state.voicesError = null;
            state.voicesFetched = false;
            state.voicesFilter = { search: '', category: null };
          });
        },

        // Subtitle Settings Actions
        updateSubtitleSettings: (updates) => {
          set((state) => {
            Object.assign(state.subtitleSettings, updates);
          });
        },

        updateSubtitleDefaultStyle: (updates) => {
          set((state) => {
            Object.assign(state.subtitleSettings.defaultStyle, updates);
          });
        },

        setGlobalSubtitleEnabled: (enabled) => {
          set({ globalSubtitleEnabled: enabled });
        },

        setSubtitleSettingsOpen: (open) => {
          set({ isSubtitleSettingsOpen: open });
        },

        // Scene Subtitle Actions
        updateSceneSubtitleText: (sceneId, text) => {
          set((state) => {
            const scene = state.scenes.find((s) => s.id === sceneId);
            if (scene) {
              // NFD(分解形)の濁点・半濁点をNFC(合成形)に正規化
              scene.subtitleText = text.normalize('NFC');
            }
          });
        },

        toggleSceneSubtitle: (sceneId, enabled) => {
          set((state) => {
            const scene = state.scenes.find((s) => s.id === sceneId);
            if (scene) {
              scene.subtitleEnabled = enabled;
            }
          });
        },

        updateSceneSubtitleStyle: (sceneId, style) => {
          set((state) => {
            const scene = state.scenes.find((s) => s.id === sceneId);
            if (scene) {
              scene.subtitleStyle = { ...scene.subtitleStyle, ...style };
            }
          });
        },

        resetSceneSubtitleStyle: (sceneId) => {
          set((state) => {
            const scene = state.scenes.find((s) => s.id === sceneId);
            if (scene) {
              scene.subtitleStyle = undefined;
            }
          });
        },

        resetAllSceneSubtitleStyles: () => {
          set((state) => {
            state.scenes.forEach((scene) => {
              scene.subtitleStyle = undefined;
            });
          });
        },

        copyTextToSubtitle: (sceneId) => {
          set((state) => {
            const scene = state.scenes.find((s) => s.id === sceneId);
            if (scene) {
              scene.subtitleText = scene.text;
            }
          });
        },

        copyAllTextsToSubtitles: () => {
          set((state) => {
            state.scenes.forEach((scene) => {
              scene.subtitleText = scene.text;
            });
          });
        },

        setGenerationStatus: (status) => {
          set((state) => {
            Object.assign(state.generationStatus, status);
          });
        },

        resetGeneration: () => {
          set({ generationStatus: initialGenerationStatus });
        },

        registerCancelCallback: (callback) => {
          set({ cancelCallback: callback });
        },

        cancelGeneration: () => {
          const { cancelCallback } = get();
          if (cancelCallback) {
            cancelCallback();
          }
          // キャンセル後はコールバックをクリア
          set({ cancelCallback: null, generationStatus: initialGenerationStatus });
        },

        showCompletionDialog: (title, description) => {
          set({ completionDialog: { open: true, title, description } });
        },

        hideCompletionDialog: () => {
          set({ completionDialog: initialCompletionDialog });
        },

        showErrorConfirmDialog: (title, description) => {
          return new Promise<boolean>((resolve) => {
            set({
              errorConfirmDialog: {
                open: true,
                title,
                description,
                resolveCallback: resolve,
              },
            });
          });
        },

        resolveErrorConfirmDialog: (shouldContinue) => {
          const { errorConfirmDialog } = get();
          if (errorConfirmDialog.resolveCallback) {
            errorConfirmDialog.resolveCallback(shouldContinue);
          }
          set({ errorConfirmDialog: initialErrorConfirmDialog });
        },

        hideErrorConfirmDialog: () => {
          set({ errorConfirmDialog: initialErrorConfirmDialog });
        },
        };
      }),
      {
        name: 'videocreator-settings',
        version: 3,
        partialize: (state) => ({
          settings: state.settings,
          videoSettings: state.videoSettings,
          subtitleSettings: state.subtitleSettings,
          globalSubtitleEnabled: state.globalSubtitleEnabled,
        }),
        migrate: (persistedState: unknown, version: number) => {
          let state = persistedState as {
            settings: AppSettings;
            videoSettings: VideoSettings;
            subtitleSettings?: SubtitleSettings;
            globalSubtitleEnabled?: boolean;
          };

          // v1 -> v2: 音量正規化設定を追加
          if (version < 2) {
            const oldVideoSettings = state.videoSettings as Omit<VideoSettings, 'enableAudioNormalization' | 'targetLoudness'>;
            state = {
              ...state,
              videoSettings: {
                ...oldVideoSettings,
                enableAudioNormalization: true,
                targetLoudness: -16,
              },
            };
          }

          // v2 -> v3: テロップ設定を追加
          if (version < 3) {
            state = {
              ...state,
              subtitleSettings: DEFAULT_SUBTITLE_SETTINGS,
              globalSubtitleEnabled: false,
            };
          }

          return state;
        },
      }
    ),
    {
      name: 'VideoCreator App Store',
      enabled: import.meta.env.DEV,
    }
  )
);
